import type { IDivisionResponse, IDivisionsPayload } from "../types/myTeam";

/** Arrays inside `subDivisions` that may hold child division objects (POST/GET shape variants). */
const SUB_DIVISION_ARRAY_KEYS = [
  "divisionRequests",
  "divisionResponseList",
  "divisionResponses",
  "divisionRequestList",
  "nestedDivisions",
  "children",
  "groups",
] as const;

/**
 * Collect every division object in the tree (for resolving `childDivisions: string[]` id refs).
 */
function walkCollectDivisionObjects(node: unknown, out: IDivisionResponse[]): void {
  if (!node || typeof node !== "object") return;
  const d = node as IDivisionResponse;
  if (typeof d.divisionId === "string" && d.divisionId.trim()) {
    out.push(d);
  }
  const rec = node as Record<string, unknown>;

  const ch = rec.childDivisions;
  if (Array.isArray(ch)) {
    for (const item of ch) {
      if (typeof item === "string") continue;
      walkCollectDivisionObjects(item, out);
    }
  }

  const sub = rec.subDivisions;
  if (sub && typeof sub === "object") {
    const subRec = sub as Record<string, unknown>;
    for (const key of SUB_DIVISION_ARRAY_KEYS) {
      const arr = subRec[key];
      if (!Array.isArray(arr)) continue;
      for (const item of arr) {
        walkCollectDivisionObjects(item, out);
      }
    }
  }
}

function buildDivisionRegistry(topLevel: IDivisionResponse[]): Map<string, IDivisionResponse> {
  const collected: IDivisionResponse[] = [];
  for (const d of topLevel) {
    walkCollectDivisionObjects(d, collected);
  }
  const map = new Map<string, IDivisionResponse>();
  for (const d of collected) {
    const id = d.divisionId?.trim();
    if (id) map.set(id, d);
  }
  return map;
}

function arraysFromSubDivisions(sub: unknown): unknown[][] {
  if (!sub || typeof sub !== "object") return [];
  const obj = sub as Record<string, unknown>;
  return SUB_DIVISION_ARRAY_KEYS.map((k) => obj[k]).filter(Array.isArray) as unknown[][];
}

/**
 * Merge `childDivisions` (objects and/or string ids) + `subDivisions.*` into `childDivisions` objects.
 */
export function normalizeDivisionResponse(
  raw: IDivisionResponse,
  registry: Map<string, IDivisionResponse>
): IDivisionResponse {
  const rec = raw as Record<string, unknown>;
  const mergedChildren: IDivisionResponse[] = [];
  const seenChildIds = new Set<string>();
  const parentId = String(raw.divisionId ?? "root");

  const pushChild = (child: IDivisionResponse | null) => {
    if (!child) return;
    const id = child.divisionId?.trim();
    const key = id || `${parentId}:${child.divisionName}:${mergedChildren.length}`;
    if (id && seenChildIds.has(id)) return;
    if (id) seenChildIds.add(id);
    mergedChildren.push(child);
  };

  const fromChild = Array.isArray(rec.childDivisions) ? rec.childDivisions : [];
  fromChild.forEach((item, i) => {
    if (typeof item === "string") {
      const id = item.trim();
      if (!id) return;
      const found = registry.get(id);
      const base: IDivisionResponse =
        found ??
        ({
          divisionId: id,
          divisionName: `Group ${i + 1}`,
        } as IDivisionResponse);
      pushChild(normalizeDivisionResponse(base, registry));
      return;
    }
    if (item && typeof item === "object") {
      pushChild(normalizeDivisionResponse(item as IDivisionResponse, registry));
    }
  });

  const sub = rec.subDivisions;
  if (sub && typeof sub === "object") {
    let idx = fromChild.length;
    for (const arr of arraysFromSubDivisions(sub)) {
      for (const n of arr) {
        if (typeof n === "string") {
          const id = n.trim();
          if (!id) continue;
          const found = registry.get(id);
          const base: IDivisionResponse =
            found ??
            ({
              divisionId: id,
              divisionName: `Group ${idx + 1}`,
            } as IDivisionResponse);
          pushChild(normalizeDivisionResponse(base, registry));
          idx += 1;
          continue;
        }
        if (n && typeof n === "object") {
          pushChild(normalizeDivisionResponse(n as IDivisionResponse, registry));
          idx += 1;
        }
      }
    }
  }

  const next: IDivisionResponse = { ...raw };
  if (mergedChildren.length > 0) {
    next.childDivisions = mergedChildren;
  }
  return next;
}

/** Normalize roots; builds an id registry from the full tree first (for `childDivisions: string[]`). */
export function normalizeDivisionList(divisions: IDivisionResponse[]): IDivisionResponse[] {
  const registry = buildDivisionRegistry(divisions);
  return divisions.map((d) => normalizeDivisionResponse(d, registry));
}

/** Normalize top-level getDivisions payload so each division has merged `childDivisions`. */
export function normalizeDivisionsPayload(payload: IDivisionsPayload | IDivisionResponse[] | unknown): IDivisionsPayload {
  if (Array.isArray(payload)) {
    const list = payload as IDivisionResponse[];
    return { divisionResponseList: normalizeDivisionList(list) };
  }
  if (!payload || typeof payload !== "object") {
    return { divisionResponseList: [] };
  }
  const obj = payload as IDivisionsPayload & {
    divisionResponses?: IDivisionResponse[];
  };
  const list =
    obj.divisionResponseList ??
    obj.divisionResponses ??
    [];
  const arr = Array.isArray(list) ? list : [];
  return {
    ...obj,
    divisionResponseList: normalizeDivisionList(arr),
  };
}
